/*@flow*/
import assert from './assert';
import getTypeName from './getTypeName';
import isIdentity from './isIdentity';
import isArray from './isArray';
import create from './create';
import is from './is';
import update from './update';
import type { TypeT } from './types';
import withOptions from './withOptions';

type TupleType = TypeT<{ types: TypeT<*>[] }>;

function getDefaultName(types: TypeT<*>[]): string {
  return '[' + types.map(getTypeName).join(', ') + ']';
}

function tuple(types: TypeT<*>[], name?: string): TupleType {
  let displayName = name || getDefaultName(types);
  let identity = types.every(isIdentity);

  function Tuple<T: any>(value: T[], path?: string[]): T[] {
    if (process.env.NODE_ENV === 'production') {
      if (identity) return value;
    }

    let path1 = path || [displayName];

    if (process.env.NODE_ENV !== 'production') {
      assert(isArray(value) && value.length === types.length, function() {
        return (
          'Invalid value ' +
          assert.stringify(value) +
          ' supplied to ' +
          path1.join('/') +
          ' (expected an array of length ' +
          types.length +
          ')'
        );
      });
    }

    let idempotent = true;
    let ret = [];
    types.forEach((_, i) => {
      let expected = types[i];
      let actual = value[i];

      let instance = create(
        expected,
        actual,
        process.env.NODE_ENV !== 'production'
          ? path1.concat(i + ': ' + getTypeName(expected))
          : null
      );
      idempotent = idempotent && actual === instance;
      ret.push(instance);
    });

    if (idempotent) {
      // implements idempotency
      ret = value;
    }

    if (process.env.NODE_ENV !== 'production') {
      Object.freeze(ret);
    }

    return ret;
  }

  Tuple.meta = {
    kind: 'tuple',
    types,
    name,
    identity,
  };

  Tuple.displayName = displayName;

  Tuple.is = (x: any): boolean => {
    return (
      isArray(x) &&
      x.length === types.length &&
      types.every((type, i) => is(x[i], type))
    );
  };

  Tuple.update = function<T: any[]>(instance: T[], patch: {}): T[] {
    return Tuple(update<T[]>(instance, patch));
  };

  withOptions(Tuple);

  return ((Tuple: any): TupleType);
}

tuple.getDefaultName = getDefaultName;
export default tuple;
